import React, { useState } from "react";
import { CircleAlert, CircleCheckBig } from "lucide-react";
import { clsx } from "clsx";

import { toast } from "@/design-system/components/Toast/Toast";

import { useApiQuery } from "@/api";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { makeRequest } from "@/util";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import { DestinationValidity } from "@/destinations/validation";
import useCurrentProject from "@/hooks/use-project";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { EnableDestinationStepProps } from "../stepProps";
import { blocksTurnOn, getValidityIfTurnedOn } from "../turnOn";
import { useEventsForDestination } from "../useEventsForDestination";

function ChecklistItem({
  ok,
  children,
}: React.PropsWithChildren<{ ok: boolean }>) {
  const Icon = ok ? CircleCheckBig : CircleAlert;
  return (
    <li className="flex items-center gap-3">
      <Icon
        className={clsx(
          "size-icon-md shrink-0",
          ok ? "text-status-success" : "text-status-warning",
        )}
        strokeWidth={2}
        aria-hidden
      />
      <span className="text-body-sm text-textPrimary">{children}</span>
    </li>
  );
}

export function TurnOnStep({
  data,
  context,
  goNext,
  goToStep,
}: EnableDestinationStepProps) {
  const { saasDestination: destination, configuredDestination } = context;
  const events = useEventsForDestination(data, context);
  const accountMetadata = useAccountMetadata();
  const project = useCurrentProject();
  const { data: consentConfigsData } = useApiQuery([
    "get_consent_management_configs",
    {},
  ]);
  const canWrite = useUserHasAccess(
    RBACDomain.destinationConfiguration,
    RBACAccessLevel.write,
  );
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const [saving, setSaving] = useState(false);
  const name = destination?.name ?? "the destination";

  async function turnOn(destinationID: string) {
    setSaving(true);
    try {
      await makeRequest("toggle_whole_destination", {
        dest_id: destinationID,
        state: true,
      });
      await refetchDestinations();
      goNext();
    } catch (error) {
      toast.error(`Couldn’t turn on ${name}. Try again.`);
      console.error(error);
      setSaving(false);
    }
  }

  const enabled = !!configuredDestination?.enabled;
  const validityLoaded = accountMetadata.loaded && project.loaded;
  const validity =
    destination && configuredDestination && validityLoaded
      ? getValidityIfTurnedOn({
          accountMetadata,
          project,
          configuredDestination,
          destination,
          consentConfigs: consentConfigsData?.configs,
        })
      : undefined;
  const isBlocked = !!validity && blocksTurnOn(validity);
  const needsAllowlist =
    validity?.validity ===
    DestinationValidity.totallyInvalidMissingAllowlistWhenHIPAA;

  const disabledReason = !configuredDestination
    ? "Connect the destination first"
    : canWrite === false
      ? "Ask an admin for permission to turn on destinations"
      : isBlocked
        ? `Fix the ${name} configuration first`
        : undefined;

  return (
    <WizardStepLayout
      title={`Turn on ${name}`}
      description="Review the setup, then start sending events."
      secondaryActions={
        needsAllowlist &&
        !enabled && (
          <Button variant="outline" onClick={() => goToStep("allowlist")}>
            Review allowlist
          </Button>
        )
      }
      primaryAction={{
        label: enabled ? "Continue" : `Turn on ${name}`,
        onClick: () => {
          if (enabled || !configuredDestination) {
            goNext();
            return;
          }
          void turnOn(configuredDestination.id);
        },
        loading: saving,
        disabled: !enabled && (!validityLoaded || !!disabledReason),
        disabledReason,
      }}
    >
      {canWrite === false && !enabled && (
        <Banner type="warning">
          You don’t have permission to turn on destinations. Ask an admin to
          turn on {name}.
        </Banner>
      )}
      <ul className="flex flex-col gap-3">
        <ChecklistItem ok={!!configuredDestination}>
          {configuredDestination
            ? "Account connected"
            : "Account not connected"}
        </ChecklistItem>
        <ChecklistItem ok={events.length > 0}>
          {events.length === 1
            ? "1 event will be sent"
            : `${events.length} events will be sent`}
        </ChecklistItem>
        <ChecklistItem ok={enabled}>
          {enabled ? "Destination is on" : "Destination is off"}
        </ChecklistItem>
      </ul>
      {isBlocked && destination && configuredDestination && (
        <Banner type="error">
          {validity?.warningMessage ??
            `${name} can’t receive events with its current configuration.`}{" "}
          <WizardLink
            to={`${destination.configPageUrl()}/${configuredDestination.id}`}
            className="cursor-pointer underline"
          >
            Open {name} settings
          </WizardLink>
        </Banner>
      )}
      {!isBlocked && validity?.warningMessage && (
        <Banner type="warning">{validity.warningMessage}</Banner>
      )}
    </WizardStepLayout>
  );
}
